import type { PaymentsPropertiesResult } from '@centecomm/portal-service';
import { usePayments } from '@centecomm/portal-service-react';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { LoadStatus } from '../../../Types';
import type { PaymentsActionTypes } from '../actions';
import { PaymentsActions } from '../actions';
import { propertiesLoadStatus, sortedPropertiesList } from '../selectors';
import type { PaymentsProperty } from '../Types/PaymentsProperty';

type PropertiesStateResult = {
    areLoaded: boolean;
    areLoading: boolean;
    hasError: boolean;
    loadState: LoadStatus;
    properties: PaymentsProperty[];
};

// eslint-disable-next-line import/prefer-default-export
export const usePropertiesFromPaymentsState = (enabled = true): PropertiesStateResult => {
    const dispatch = useDispatch();

    const { getPaymentsProperties } = usePayments();

    const loadState: LoadStatus = useSelector(propertiesLoadStatus);
    const properties: PaymentsProperty[] = useSelector(sortedPropertiesList);

    React.useEffect(() => {
        if (!enabled) return;

        if (loadState === LoadStatus.INITIAL_STATE || loadState === LoadStatus.ERROR) {
            dispatch({
                type: PaymentsActions.LOAD_PAYMENTS_PROPERTIES,
            });

            getPaymentsProperties()
                .then((propertiesResult: PaymentsPropertiesResult[]) => {
                    dispatch({
                        type: PaymentsActions.SET_PAYMENTS_PROPERTIES,
                        payload: propertiesResult.map(
                            ({ id, name, address, occupants }) =>
                                ({
                                    id,
                                    name,
                                    address: {
                                        streetAddress: address.streetAddress,
                                        city: address.city,
                                        state: address.state,
                                        zipCode: address.zipCode,
                                    },
                                    occupants: occupants.map((occupant) => ({
                                        id: occupant.id,
                                        name: occupant.name,
                                        phone: occupant.phone,
                                        activeStartDate: occupant.activeStartDate,
                                        activeEndDate: occupant.activeEndDate,
                                        leaseStart: occupant.leaseStart,
                                        leaseEnd: occupant.leaseEnd,
                                        isActive: occupant.isActive,
                                        isPast: occupant.isPast,
                                        isFuture: occupant.isFuture,
                                    })),
                                } as PaymentsProperty),
                        ),
                    } as PaymentsActionTypes);
                })
                .catch((error) => {
                    dispatch({
                        type: PaymentsActions.SET_PAYMENTS_PROPERTIES_ERROR_STATE,
                        payload: {
                            errorMessage: error?.code ? error.code : 'We were not able to load properties.',
                        },
                    });
                });
        }
    }, [loadState, enabled]);

    return {
        areLoaded: loadState === LoadStatus.LOADED,
        areLoading: loadState === LoadStatus.PENDING,
        hasError: loadState === LoadStatus.ERROR,
        loadState,
        properties,
    };
};
